import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ChatInterface.css';

// Services
import chatService from '../../services/chatService';

// Components
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';

const ChatInterface = ({ onMenuClick, isMobile }) => {
  const [messages, setMessages] = useState([
    { id: 1, text: "Hello! I'm your SageMindLabs agent. I can answer questions from your knowledge base, take actions via tools, and cite my sources. How can I help you today?", sender: 'bot', timestamp: new Date() }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sendingRef = useRef(false);
  const navigate = useNavigate();
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Focus input on component mount and check authentication
  useEffect(() => {
    inputRef.current.focus();
    checkAuthentication();
  }, []);
  
  // Check if user is authenticated
  const checkAuthentication = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user && user.access_token) {
          return true;
        }
      } catch (e) {
        console.error('Error parsing user from localStorage', e);
      }
    }
    return false;
  };
  
  // Handle login redirect
  const handleLoginRedirect = () => {
    navigate('/login', { state: { returnUrl: window.location.pathname } });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSubmit = async (e, selectedQuestion = inputValue) => {
    e?.preventDefault();
    const question = typeof selectedQuestion === 'string' ? selectedQuestion.trim() : '';
    if (!question || sendingRef.current) return;
    
    // Check if user is authenticated
    if (!checkAuthentication()) {
      setShowLoginPrompt(true);
      return;
    }
    
    sendingRef.current = true;
    // Add user message
    const userMessage = {
      id: messages.length + 1,
      text: question,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    
    try {
      // Get user ID from localStorage or generate a temporary one
      const userId = localStorage.getItem('userId') || `temp-${Date.now()}`;

      
      // Create a session ID if one doesn't exist
      if (!localStorage.getItem('sessionId')) {
        localStorage.setItem('sessionId', `session-${Date.now()}`);
      }
      
      // Send message using chatService
      const response = await chatService.sendMessage(
        userMessage.text,
        5, // top_k parameter
        userId
      );
      
      if (typeof response?.answer !== 'string' || !response.answer.trim()) {
        throw new Error('Invalid response from server');
      }
      
      // Add bot response
      const botResponse = {
        id: messages.length + 2,
        text: response.answer,
        sender: 'bot',
        timestamp: new Date(),
        contexts: response.contexts || [],
        // Use followUps from response (camelCase as returned by backend)
        followUps: response.followUps || response.follow_ups || [],
        // SageMindLabs: agent artifacts
        citations: response.citations || [],
        toolCalls: response.toolCalls || [],
        usedTools: response.usedTools || []
      };
      
      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      console.error('Chat message could not be sent');
      
      // Create a more detailed error message
      let errorText = "Sorry, I'm having trouble processing your request right now. ";
      
      if (error.message === 'Network Error') {
        errorText += "There seems to be a connection issue with the server. Please check your internet connection and try again.";
      } else if (error.response?.status === 401) {
        errorText += "Your session has expired. Please log in again.";
      } else if (error.code === 'ECONNABORTED') {
        errorText += "The request timed out. The server might be under heavy load.";
      } else {
        errorText += "Please try again later.";
      }
      
      // Add error message
      const errorMessage = {
        id: messages.length + 2,
        text: errorText,
        sender: 'bot',
        isError: true,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      sendingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleVoiceInput = () => {
    // Voice input functionality would be implemented here
    alert('Voice input feature coming soon!');
  };

  // Function to handle follow-up question clicks
  const handleFollowUpClick = (question) => {
    if (!question || sendingRef.current) return;
    // Submit the follow-up question automatically
    handleSubmit(null, question);
  };

  // Toggle sidebar on mobile
  const handleMenuClick = () => {
    if (onMenuClick) {
      onMenuClick();
    }
  };

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <button className="menu-button" onClick={handleMenuClick} aria-label="Toggle menu">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
        <h2>SageMindLabs Agent</h2>
        <div className="header-actions">
          {/* Add any header actions here */}
        </div>
      </div>
      
      <div className="messages-container" role="log" aria-live="polite" aria-relevant="additions">
        {messages.map(message => (
          <MessageBubble 
            key={message.id} 
            message={message}
            onFollowUpClick={handleFollowUpClick}
            followUpsDisabled={isLoading || message.id !== messages[messages.length - 1]?.id}
          />
        ))}
        
        {isLoading && <TypingIndicator />}
        
        {showLoginPrompt && (
        <div className="login-prompt">
          <div className="login-prompt-content">
            <p>You need to be logged in to use the chat. Please log in to continue.</p>
            <button onClick={handleLoginRedirect} className="login-button">Log In</button>
            <button onClick={() => setShowLoginPrompt(false)} className="cancel-button">Cancel</button>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
      </div>
      
      <form onSubmit={handleSubmit} className="input-area">
        <label htmlFor="chat-message-input" className="sr-only">Ask a question</label>
        <input
          id="chat-message-input"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Ask a question…"
          ref={inputRef}
          className="message-input"
          autoComplete="off"
          enterKeyHint="send"
        />
        <button type="button" onClick={handleVoiceInput} className="voice-button" aria-label="Voice input">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
            <line x1="12" y1="19" x2="12" y2="23"></line>
            <line x1="8" y1="23" x2="16" y2="23"></line>
          </svg>
        </button>
        <button type="submit" className="send-button" aria-label="Send message" disabled={isLoading || !inputValue.trim()}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 2L11 13"></path>
            <path d="M22 2l-7 20-4-9-9-4 20-7z"></path>
          </svg>
        </button>
      </form>
    </div>
  );
};

export default ChatInterface;
